import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import Modal from "@/components/overlays/Modal";
import acquisitionClient from "@/services/acquisitionApiClient";
import RoundButton from "@/components/forms/RoundButton";
import { Link } from "react-router";
import { Input } from "@/components/ui/input";
import { authUser } from "@/_actions/authActions";
import { EyeOff, Eye, CircleAlert } from "lucide-react";
import { getProfile } from "@/_actions/userActions";
import { usePostHog } from "posthog-js/react";
import { Spinner } from "@/components/ui/spinner";
import SignupLogo from "/public/images/signup-logo.svg";

import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Correo electrónico inválido")
    .required("Ingresa un correo electrónico válido"),
  phone: Yup.string().required("Ingresa tu número de teléfono."),
  name: Yup.string().required("Ingresa tu nombre."),
  client_type: Yup.string()
    .oneOf(["real_estate", "developer", "advertiser"], "Selecciona un tipo de cuenta válido")
    .required("Selecciona un tipo de cuenta"),
  password: Yup.string()
    .min(8, "La contraseña debe tener al menos 8 caracteres")
    .required("Ingresa una contraseña"),
});

export default function AccountCreationForm({ callBack }) {
  const [showAlert, setShowAlert] = useState(false);
  const [showConflictMessage, setShowConflictMessage] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const posthog = usePostHog();

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: "",
      phone: "",
      name: "",
      client_type: "real_estate",
      password: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      await acquisitionClient.post("/customers/auto-registration/", values);

      // Sign the user into Cognito so apiClient (which uses the Cognito JWT)
      // works in later wizard steps. The verify-email handoff happens in a
      // different tab but Amplify stores the session in localStorage, which
      // is shared across tabs of the same browser.
      await authUser(values.email, values.password);
      const profileData = await getProfile();

      // Marker used by verify-email to detect whether the verification link
      // was opened on the same browser the signup started in.
      localStorage.setItem("signup_pending", "true");

      setRegisteredEmail(values.email);
      setShowVerifyModal(true);

      if (posthog && profileData?.[0]) {
        const p = profileData[0];
        const phone = p.contact?.whatsapp || p.contact?.mobile;
        const name = `${p.first_name} ${p.last_name}`;
        posthog.identify(p.id, { email: p.email, name, type: "pro_user", phone });
        posthog.capture("Signup - Pro", {
          step: 1,
          stepName: "account",
          email: p.email,
          name,
          type: "pro_user",
          phone,
          clientType: values.client_type,
        });
      }
    } catch (err) {
      console.error("Registration error:", err);

      if (err.response) {
        console.error("Error response:", err.response);

        if (err.response.status === 409) {
          setShowConflictMessage(true);
        } else if (err.response.status >= 400) {
          setShowAlert(true);
        }
      } else if (err.request) {
        console.error("Error request:", err.request);
        setShowAlert(true);
      } else {
        console.error("Error message:", err.message);
        setShowAlert(true);
      }
    }
  };

  return (
    <div>
      <Form {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 max-w-screen-md mx-auto"
        >
          {/* Nombre */}

          <div className="bg-neutral-100 border-gray-900 border rounded-xl p-10 space-y-3">
            <h3 className="text-xl">Crea tu cuenta</h3>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="name">Nombre</FormLabel>
                  <FormControl>
                    <Input
                      id="name"
                      className="bg-white"
                      placeholder="Escribe tu nombre completo"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="md:grid grid-cols-2 gap-4">
              {/* Correo electrónico */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="email">Correo electrónico</FormLabel>
                    <FormControl>
                      <Input
                        id="email"
                        className="bg-white"
                        type="email"
                        placeholder="correo@ejemplo.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Teléfono */}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="phone">Teléfono</FormLabel>
                    <FormControl>
                      <Input
                        id="phone"
                        className="bg-white"
                        type="tel"
                        placeholder="Ingresa tu número de teléfono"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Tipo de cuenta */}
            <FormField
              control={form.control}
              name="client_type"
              render={({ field }) => (
                <FormItem>
                  <Input type="hidden" {...field} />
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Contraseña */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="password">Contraseña</FormLabel>
                  <FormControl>
                    <div className="flex w-full  items-center space-x-2">
                      <Input
                        id="password"
                        className="bg-white"
                        placeholder="Escribe tu contraseña"
                        type={showPassword ? "text" : "password"}
                        {...field}
                      />
                      <Button type="button" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          <EyeOff onClick={() => setShowPassword(false)} />
                        ) : (
                          <Eye onClick={() => setShowPassword(true)} />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormDescription>Usa mínimo 8 caracteres para que sea más segura</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <p className="text-sm">
              Al crear la cuenta, acepto los{" "}
              <a
                className="text-blue-500 underline"
                href="https://www.casasyterrenos.com/terminos-y-condiciones"
              >
                Términos de uso{" "}
              </a>{" "}
              de Casas y Terrenos.
            </p>
          </div>

          <div className="flex justify-center">
            <Link to="/login" className="text-black font-bold hover:underline text-lg">
              Ya tengo cuenta
            </Link>
          </div>

          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? (
              <>
                <Spinner /> Cargando...
              </>
            ) : (
              "Crear cuenta"
            )}
          </Button>
        </form>
      </Form>

      <Modal open={showAlert}>
        <div className="flex flex-col items-center justify-center p-8">
          <span className="mb-9 text-center text-xl ">
            Ya existe una cuenta con este correo electrónico.
          </span>
          <div className="z-10 w-full" onClick={() => setShowAlert(false)}>
            <RoundButton active={true} text={"Aceptar"} />
          </div>
        </div>
      </Modal>
      <Modal open={showConflictMessage} setOpen={setShowConflictMessage}>
        <div className="w-full pl-4 pt-4"></div>
        <div className="flex flex-col items-center justify-center px-6 pb-6">
          <CircleAlert className=" mb-3 w-20 text-yellow-cyt-alt" />
          <span className="mb-2 text-center font-bold">Ya existe un usuario con ese correo.</span>
          <span className="mb-4 text-center text-sm">
            Intenta crear tu cuenta con otro correo o{" "}
            <Link className="text-blue-700 underline" to="/forgot-password">
              restaura tu contraseña.
            </Link>
          </span>
        </div>
      </Modal>

      <Modal open={showVerifyModal} setOpen={setShowVerifyModal}>
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div
            className="bg-gray-800 rounded-2xl p-4 mb-6 relative w-24 h-24 flex items-center justify-center overflow-hidden"
            style={{ borderRadius: "24px 24px 0 24px" }}
          >
            <div className="bg-white rounded-lg p-2 z-10 shadow-lg">
              <img src={SignupLogo} alt="Signup Logo" className="w-12 h-12" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-4">Confirma tu cuenta</h2>
          <p className="text-gray-600">Te enviamos un enlace de validación al correo</p>
          <p className="text-gray-900 font-medium mb-6">{registeredEmail}</p>
          <p className="text-gray-600 mb-8">
            Haz clic en él para empezar a usar tu plataforma PRO.
          </p>
          <Button
            variant="outline"
            className="rounded-full px-8 py-6 text-base"
            onClick={() => setShowVerifyModal(false)}
          >
            Cerrar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
